import { action, computed, makeObservable, observable } from "mobx"

import type { IEpochRepository, IEpochsStore, IEpochStore } from "./interfaces"
import type { EpochOrchestrator } from "./epoch-orchestrator"

export class EpochsPageStore implements IEpochsStore {
  minEpochNumber: number = 100

  constructor(
    private readonly epochRepository: IEpochRepository,
    private readonly epochOrchestrator: EpochOrchestrator,
  ) {
    makeObservable(this, {
      minEpochNumber: observable,
      currentEpoch: computed,
      allEpochs: computed,
      setMinEpochNumber: action,
      startHistoricEpochsQuery: action,
      stopHistoricEpochsQuery: action,
      clearHistoricEpochs: action,
    })
  }

  /**
   * Get the current (latest) epoch from repository
   */
  get currentEpoch(): IEpochStore | undefined {
    return this.epochRepository.getCurrent()
  }

  /**
   * Get all epochs from repository (includes current + historic)
   */
  get allEpochs(): IEpochStore[] {
    return this.epochRepository.getAll()
  }

  /**
   * Update the minimum epoch number filter
   * Used for future "all epochs" view
   */
  setMinEpochNumber(epochNumber: number): void {
    this.minEpochNumber = epochNumber
    // If historic query is running, it will use the new minEpochNumber on next execution
  }

  /**
   * Start the historic epochs query
   * Called when analytics page mounts
   */
  startHistoricEpochsQuery(): void {
    this.epochOrchestrator.startHistoricEpochsQuery(this.minEpochNumber)
  }

  /**
   * Stop the historic epochs query
   * Called when analytics page unmounts
   */
  stopHistoricEpochsQuery(): void {
    this.epochOrchestrator.stopHistoricEpochsQuery()
  }

  /**
   * Clear historic epochs from repository
   * Called when analytics page unmounts
   * Keeps only the current epoch
   */
  clearHistoricEpochs(): void {
    this.epochRepository.clearHistoric()
  }

  /**
   * Toggle between "All Epochs" (epoch 0+) and "Recent Epochs" (epoch 100+)
   * @param showAllEpochs - true for epoch 0+, false for epoch 100+
   */
  toggleAllEpochs(showAllEpochs: boolean): void {
    const newMinEpochNumber = showAllEpochs ? 0 : 100
    this.setMinEpochNumber(newMinEpochNumber)
    this.clearHistoricEpochs()
    this.epochOrchestrator.restartHistoricEpochsQuery(newMinEpochNumber)
  }
}
